import { ChangeDetectionStrategy, Component, inject, Input, SimpleChanges } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IProductDataV2 } from '@df/catalog/product/product';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { Customer } from '@df/session/api/customer';
import { CustomerModel } from '@df/session/api/customer.model';
import { PriceComponent } from '@df/ui/price/price.component';
import { PriceService } from '@df/ui/price/price.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import sum from 'lodash-es/sum';
import { Product } from '../../catalog/product/product.decorator';
import { TradeService } from '../../checkout/module/trade/trade.service';
@Component({
    selector: 'become-pro-banner2',
    templateUrl: './become-pro-banner-2.html',
    styleUrls: ['./become-pro-banner-2.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [PriceComponent, RouterLink, TranslationModule]
})
export class BecomeProBanner2Component extends UiComponent {
    private basket = inject(Customer)?.basket;
    private tradeService = inject(TradeService);
    private priceService = inject(PriceService);
    private customeModel = inject(CustomerModel);
    private productConfiguration = inject(ProductConfiguration, { optional: true });

    protected discount: number = 0;
    protected orgPrice: number = 0;
    protected discountPercent: number = 0;

    /**
     * Modes:
     * - onAccountCreated: show on account created
     * - rrp: show saving vs rrp
     * - offer: show offer to become pro
     */
    @Input({
        required: true
    })
    mode!: 'onAccountCreated' | 'rrp' | 'offer';

    /**
     * Only set in basket item
     */
    @Input()
    source?: BasketItem[];

    /**
     * Only set when no configuration is present
     */
    @Input()
    product?: Product<IProductDataV2>;

    override ngOnInit(): void {
        this.subscriptions.add(this.priceService.catalogPricesInclTax$.subscribe(() => this.update()));
        this.customeModel.obs.data.subscribe(() => this.update());
        if (this.productConfiguration) {
            this.subscriptions.add(this.productConfiguration.product$.subscribe(() => this.update()));
            this.subscriptions.add(this.productConfiguration.qty$.subscribe(() => this.update()));
        } else {
            this.subscriptions.add(this.basket.basketUpdateSubject.subscribe(() => this.update()));
        }
        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['product'] || changes['source']) {
            this.update();
        }
        super.ngOnChanges(changes);
    }

    private update() {
        const discount = this.canUse() ? this.calc('tradeDiscount') : 0;
        if (discount !== this.discount) {
            this.discount = discount;
            this.orgPrice = this.calc('orgPrice');
            this.discountPercent = this.discount ? Math.round((this.discount / this.orgPrice) * 100) : 0;
            this.detectChanges();
        }
    }

    /**
     * If banner can be used
     * if trade customer then only when accouiredDiscountMode is presented
     * if non-trade customer then only when showing potential profit
     */
    private canUse(): boolean {
        return this.tradeService.isTradeCustomer() ? ['onAccountCreated', 'rrp'].includes(this.mode) : this.mode === 'offer';
    }

    private calc(type: 'orgPrice' | 'tradeDiscount'): number {
        // direct source mode
        if (this.source) {
            return this.getBasketItemsPrice(this.source, type);
        }

        const product = this.product || (this.productConfiguration?.product$.value as Product);
        const qty = this.product ? 1 : this.productConfiguration?.qty$.value!;
        // forced product mode
        if (product) {
            return (
                (type === 'orgPrice'
                    ? product.calcRetailPrice(this.productConfiguration?.toBasketRequest() || { qty })
                    : product.calcTradeDiscount(this.productConfiguration?.toBasketRequest() || { qty })) * qty
            );
        }

        // basket mode
        return this.getBasketItemsPrice(this.basket.items, type);
    }

    private getBasketItemsPrice(items: BasketItem[], type: 'orgPrice' | 'tradeDiscount'): number {
        return items?.length
            ? sum(
                  items.map(
                      i =>
                          (type === 'orgPrice'
                              ? (i.product as Product).calcRetailPrice(i.data.request)
                              : (i.product as Product).calcTradeDiscount(i.data.request)) * i.qty
                  )
              )
            : 0;
    }
}
